// StaffDirectory.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Alert,
  AlertTitle,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Badge,
  Fade,
  Grow,
  Zoom,
  Skeleton,
  Drawer,
  AppBar,
  Toolbar,
  IconButton as MuiIconButton,
  Hidden,
  Container,
  Breadcrumbs,
  Link,
  CardActions,
  ButtonGroup,
  Rating,
  LinearProgress,
  Collapse,
  Chip as MuiChip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
  useScrollTrigger,
  Slide,
  Fab,
  InputBase,
  Popover,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Menu,
  ListSubheader,
  Input,
  OutlinedInput,
} from '@mui/material';

import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  BarChart as BarChartIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Group as GroupIcon,
  Celebration as CelebrationIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  EventNote as EventNoteIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  GridView as GridViewIcon,
  TableChart as TableChartIcon,
  AccountBalance as BankIcon,
  Badge as BadgeIcon,
  Cake as CakeIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  PhotoCamera as PhotoCameraIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material';

import Sidebar from '../../components/sidebars/sidebar_gm';
import TopNav from '../../components/top_bars/TopNav_inventory';

// ==================== CONSTANTS ====================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const MANAGER_ROLES = ['gm', 'manager', 'admin', 'hr', 'account', 'auditor'];

// Elegant color palette
const COLORS = {
  primary: '#1B5E20',
  primaryLight: '#4CAF50',
  primaryDark: '#0D3B0F',
  secondary: '#FF6F00',
  secondaryLight: '#FF9800',
  accent: '#9C27B0',
  accentLight: '#CE93D8',
  info: '#0288D1',
  infoLight: '#4FC3F7',
  success: '#2E7D32',
  warning: '#ED6C02',
  error: '#D32F2F',
  background: '#F5F7FA',
  surface: '#FFFFFF',
  text: '#1A1A2E',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
};

// Department colors with beautiful gradients
const DEPARTMENT_COLORS = {
  'ICT': { primary: '#2196f3', light: '#64b5f6', gradient: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)' },
  'Admin': { primary: '#4caf50', light: '#81c784', gradient: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' },
  'HR': { primary: '#ff9800', light: '#ffb74d', gradient: 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)' },
  'Account & Auditing': { primary: '#9c27b0', light: '#ce93d8', gradient: 'linear-gradient(135deg, #9c27b0 0%, #6a1b9a 100%)' },
  'Store Keeping': { primary: '#795548', light: '#a1887f', gradient: 'linear-gradient(135deg, #795548 0%, #4e342e 100%)' },
  'Security': { primary: '#f44336', light: '#ef9a9a', gradient: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)' },
  'Restaurant': { primary: '#ff5722', light: '#ffab91', gradient: 'linear-gradient(135deg, #ff5722 0%, #bf360c 100%)' },
  'Salon': { primary: '#e91e63', light: '#f48fb1', gradient: 'linear-gradient(135deg, #e91e63 0%, #880e4f 100%)' },
  'Spa': { primary: '#00bcd4', light: '#80deea', gradient: 'linear-gradient(135deg, #00bcd4 0%, #006064 100%)' },
  'Fashion & Lifestyle': { primary: '#3f51b5', light: '#9fa8da', gradient: 'linear-gradient(135deg, #3f51b5 0%, #283593 100%)' },
  'Fragrance': { primary: '#009688', light: '#80cbc4', gradient: 'linear-gradient(135deg, #009688 0%, #004d40 100%)' },
  'Marketing & Product Development': { primary: '#ffc107', light: '#ffe082', gradient: 'linear-gradient(135deg, #ffc107 0%, #ff6f00 100%)' },
  'Conference & events': { primary: '#607d8b', light: '#b0bec5', gradient: 'linear-gradient(135deg, #607d8b 0%, #37474f 100%)' },
  'Cleaning & Maintenance': { primary: '#8bc34a', light: '#dcedc8', gradient: 'linear-gradient(135deg, #8bc34a 0%, #33691e 100%)' },
  'Reception': { primary: '#ff4081', light: '#ff80ab', gradient: 'linear-gradient(135deg, #ff4081 0%, #c51162 100%)' },
  'Driver': { primary: '#7e57c2', light: '#b39ddb', gradient: 'linear-gradient(135deg, #7e57c2 0%, #4527a0 100%)' },
  'Media': { primary: '#26a69a', light: '#80cbc4', gradient: 'linear-gradient(135deg, #26a69a 0%, #004d40 100%)' },
  'Hostess': { primary: '#d4e157', light: '#e6ee9c', gradient: 'linear-gradient(135deg, #d4e157 0%, #827717 100%)' },
  'Arcade': { primary: '#ff6e40', light: '#ffab91', gradient: 'linear-gradient(135deg, #ff6e40 0%, #bf360c 100%)' },
  'Skin & Personal Care': { primary: '#ab47bc', light: '#ce93d8', gradient: 'linear-gradient(135deg, #ab47bc 0%, #6a1b9a 100%)' },
  'Makeup': { primary: '#ec407a', light: '#f48fb1', gradient: 'linear-gradient(135deg, #ec407a 0%, #880e4f 100%)' },
};

// Default department color
const DEFAULT_DEPT_COLOR = { primary: '#757575', light: '#bdbdbd', gradient: 'linear-gradient(135deg, #757575 0%, #424242 100%)' };

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '')
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[\\/]/g, '');
};

/**
 * Format currency for Nigerian Naira
 */
const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '₦0.00';
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

/**
 * Format date to readable format
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.warn('[StaffDirectory] Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Format relative time (e.g., "2 days ago")
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const now = new Date();
    if (isNaN(date.getTime())) return 'N/A';
    
    const diffSeconds = Math.floor((now - date) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    if (diffSeconds < 60) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  } catch {
    return 'N/A';
  }
};

/**
 * Get initials from name
 */
const getInitials = (fullName) => {
  if (!fullName) return 'S';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get status color for chips
 */
const getStatusColor = (status) => {
  if (status === 'Active') return 'success';
  if (status === 'Inactive') return 'error';
  return 'default';
};

/**
 * Get department color object
 */
const getDepartmentColor = (department) => {
  if (!department) return DEFAULT_DEPT_COLOR;
  return DEPARTMENT_COLORS[department] || DEFAULT_DEPT_COLOR;
};

/**
 * Generate avatar URL with fallback - FIXED for robust image handling
 * This function now properly handles profile_photo_url from the backend
 */
const getAvatarUrl = (profilePhoto, fullName, department) => {
  console.debug(`[StaffDirectory] getAvatarUrl called with:`, { profilePhoto, fullName, department });
  
  // If there's a valid profile photo URL from the database
  if (profilePhoto && typeof profilePhoto === 'string' && profilePhoto.trim() !== '') {
    let cleanPath = profilePhoto.trim();
    
    // Case 1: It's already a full URL
    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      console.debug(`[StaffDirectory] Using full URL for avatar: ${cleanPath}`);
      return cleanPath;
    }
    
    // Case 2: Path starts with /uploads/ (absolute path from server root)
    if (cleanPath.startsWith('/uploads/')) {
      const fullUrl = `${API_BASE_URL}${cleanPath}`;
      console.debug(`[StaffDirectory] Using /uploads/ path: ${fullUrl}`);
      return fullUrl;
    }
    
    // Case 3: Path starts with uploads/ (relative path)
    if (cleanPath.startsWith('uploads/')) {
      const fullUrl = `${API_BASE_URL}/${cleanPath}`;
      console.debug(`[StaffDirectory] Using uploads/ path: ${fullUrl}`);
      return fullUrl;
    }
    
    // Case 4: Just a filename (like profile_68a7362ede6c1.jpeg)
    if (!cleanPath.includes('/') && (cleanPath.includes('.jpg') || cleanPath.includes('.jpeg') || cleanPath.includes('.png') || cleanPath.includes('.gif'))) {
      const fullUrl = `${API_BASE_URL}/uploads/${cleanPath}`;
      console.debug(`[StaffDirectory] Using filename only: ${fullUrl}`);
      return fullUrl;
    }
    
    // If we have a path but none of the above, try to construct
    console.debug(`[StaffDirectory] Trying to construct URL for: ${cleanPath}`);
    return `${API_BASE_URL}/${cleanPath.replace(/^\/+/, '')}`;
  }
  
  // Fallback to avatar API if no photo
  const deptColor = getDepartmentColor(department);
  const colorHex = deptColor.primary.replace('#', '');
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || 'Staff')}&background=${colorHex}&color=fff&size=200&bold=true&length=2`;
  console.debug(`[StaffDirectory] Using fallback avatar: ${fallbackUrl}`);
  return fallbackUrl;
};

/**
 * Log security events
 */
const logSecurityEvent = (eventType, details) => {
  console.debug(`[SECURITY] StaffDirectory: ${eventType}`, details);
};

/**
 * Debounce function for search
 */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// ==================== MAIN COMPONENT ====================
const StaffDirectory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // View mode state - DEFAULT TO TABLE VIEW
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  
  // Data states
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [salaryRange, setSalaryRange] = useState([0, 1000000]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalStaff, setTotalStaff] = useState(0);
  
  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [favorites, setFavorites] = useState([]);
  const [userRole, setUserRole] = useState('gm');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  
  // Image error tracking - keyed by staff id
  const [imageErrors, setImageErrors] = useState({});
  
  // Search refs to prevent race conditions
  const isSearchingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // ==================== DERIVED VALUES ====================
  const isManager = useMemo(() => MANAGER_ROLES.includes(userRole?.toLowerCase()), [userRole]);
  
  // ==================== API CALLS ====================
  
  const getApiBaseUrl = () => {
    const baseUrl = API_BASE_URL;
    console.debug(`[StaffDirectory] API Base URL: ${baseUrl || 'default'}`);
    return baseUrl;
  };
  
  const fetchAllStaff = useCallback(async () => {
    // Don't fetch if we're currently searching
    if (isSearchingRef.current) {
      console.debug('[StaffDirectory] Skipping fetchAllStaff - search in progress');
      return;
    }
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    const startTime = performance.now();
    console.debug('[StaffDirectory] Fetching all staff');
    
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/staff_directory_api.php?action=get_all_staff&page=${page + 1}&limit=${rowsPerPage}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      console.debug(`[StaffDirectory] GET request: ${url}`);
      
      const response = await fetch(url, { signal: abortControllerRef.current.signal });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.debug(`[StaffDirectory] Response in ${(performance.now() - startTime).toFixed(2)}ms`);
      
      if (result.success) {
        const staffData = (result.data || []).map(staff => ({
          ...staff,
          full_name: sanitizeString(staff.full_name || `${staff.surname} ${staff.other_names}`),
          phone: sanitizeString(staff.phone),
          email: sanitizeString(staff.email),
          department: sanitizeString(staff.department),
          status: sanitizeString(staff.status),
          residential_address: sanitizeString(staff.residential_address),
          // Preserve the original profile_photo_url for image handling
          profile_photo_url: staff.profile_photo_url || staff.profile_photo || null,
        }));
        setStaffList(staffData);
        setTotalStaff(result.meta?.total_records || staffData.length);
        console.debug(`[StaffDirectory] Loaded ${staffData.length} staff members`);
      } else {
        throw new Error(result.message || 'Failed to load staff');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[StaffDirectory] Fetch error:', err);
        setSnackbar({ 
          open: true, 
          message: `Failed to load staff: ${err.message}`, 
          severity: 'error' 
        });
        setStaffList([]);
        setFilteredStaff([]);
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
      abortControllerRef.current = null;
    }
  }, [page, rowsPerPage, sortBy, sortOrder]);
  
  const fetchDepartments = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${baseUrl}/staff_directory_api.php?action=get_departments`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setDepartments(result.data);
        console.debug(`[StaffDirectory] Loaded ${result.data.length} departments`);
      } else {
        setDepartments([]);
        console.debug('[StaffDirectory] No departments found');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[StaffDirectory] Error fetching departments:', err);
      }
      setDepartments([]);
    }
  }, []);
  
  const fetchSummary = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${baseUrl}/staff_directory_api.php?action=get_staff_summary`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setSummary(result.data);
        console.debug('[StaffDirectory] Summary loaded');
      } else {
        setSummary(null);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[StaffDirectory] Error fetching summary:', err);
      }
      setSummary(null);
    }
  }, []);
  
  const fetchUpcomingBirthdays = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${baseUrl}/staff_directory_api.php?action=get_staff_birthdays`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setUpcomingBirthdays(result.data);
        console.debug(`[StaffDirectory] Loaded ${result.data.length} upcoming birthdays`);
      } else {
        setUpcomingBirthdays([]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[StaffDirectory] Error fetching birthdays:', err);
      }
      setUpcomingBirthdays([]);
    }
  }, []);
  
  const fetchStaffByPhone = async (phone) => {
    if (!phone) {
      console.warn('[StaffDirectory] No phone number provided');
      return;
    }
    
    setLoading(true);
    console.debug(`[StaffDirectory] Fetching staff details for ${phone}`);
    
    try {
      const baseUrl = getApiBaseUrl();
      const userProfile = localStorage.getItem('userProfile');
      let viewerName = 'Manager';
      if (userProfile) {
        try {
          const profile = JSON.parse(userProfile);
          viewerName = `${profile.first_name || ''} ${profile.other_names || ''}`.trim() || 'Manager';
        } catch (e) {
          console.error('[StaffDirectory] Error parsing user profile:', e);
        }
      }
      
      const encodedPhone = encodeURIComponent(phone);
      const encodedViewer = encodeURIComponent(viewerName);
      const url = `${baseUrl}/staff_directory_api.php?action=get_staff_by_phone&phone=${encodedPhone}&viewer_name=${encodedViewer}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // Sanitize sensitive data
        const sanitizedStaff = {
          ...result.data,
          surname: sanitizeString(result.data.surname),
          other_names: sanitizeString(result.data.other_names),
          email: sanitizeString(result.data.email),
          phone: sanitizeString(result.data.phone),
          department: sanitizeString(result.data.department),
          residential_address: sanitizeString(result.data.residential_address),
          hometown_permanent_address: sanitizeString(result.data.hometown_permanent_address),
          next_of_kin_name: sanitizeString(result.data.next_of_kin_name),
          next_of_kin_address: sanitizeString(result.data.next_of_kin_address),
          bank_name: sanitizeString(result.data.bank_name),
          account_name: sanitizeString(result.data.account_name),
          // Preserve the original profile_photo_url
          profile_photo_url: result.data.profile_photo_url || result.data.profile_photo || null,
        };
        setSelectedStaff(sanitizedStaff);
        setDetailsDrawerOpen(true);
        console.debug(`[StaffDirectory] Staff details loaded for ${sanitizedStaff.surname}`);
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message || 'Staff not found', 
          severity: 'error' 
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[StaffDirectory] Error fetching staff details:', err);
        setSnackbar({ 
          open: true, 
          message: `Error: ${err.message}`, 
          severity: 'error' 
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const searchStaff = useCallback(async (query) => {
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If query is empty or too short, fetch all staff
    if (!query || query.length < 2) {
      isSearchingRef.current = false;
      await fetchAllStaff();
      return;
    }
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Debounce the search
    searchTimeoutRef.current = setTimeout(async () => {
      isSearchingRef.current = true;
      setLoading(true);
      console.debug(`[StaffDirectory] Searching staff: ${query}`);
      
      try {
        abortControllerRef.current = new AbortController();
        
        const baseUrl = getApiBaseUrl();
        const encodedQuery = encodeURIComponent(query.trim());
        const url = `${baseUrl}/staff_directory_api.php?action=search_staff&q=${encodedQuery}`;
        
        const response = await fetch(url, { signal: abortControllerRef.current.signal });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          const searchResults = result.data.map(staff => ({
            ...staff,
            full_name: sanitizeString(staff.full_name),
            phone: sanitizeString(staff.phone),
            email: sanitizeString(staff.email),
            department: sanitizeString(staff.department),
            profile_photo_url: staff.profile_photo_url || staff.profile_photo || null,
          }));
          
          setStaffList(searchResults);
          setTotalStaff(searchResults.length);
          setPage(0);
          console.debug(`[StaffDirectory] Found ${searchResults.length} results`);
        } else {
          setStaffList([]);
          setTotalStaff(0);
          if (result.message) {
            setSnackbar({ open: true, message: result.message, severity: 'info' });
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[StaffDirectory] Search error:', err);
          setSnackbar({ 
            open: false, 
            
            severity: 'error' 
          });
        }
      } finally {
        setLoading(false);
        isSearchingRef.current = false;
        abortControllerRef.current = null;
      }
    }, 500);
  }, [fetchAllStaff]);
  
  // ==================== INITIALIZATION ====================
  useEffect(() => {
    try {
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        setUserRole(profile.user_type || 'gm');
        console.debug(`[StaffDirectory] User role: ${profile.user_type}`);
      }
      
      // Load favorites from localStorage
      const savedFavorites = localStorage.getItem('staffFavorites');
      if (savedFavorites) {
        try {
          setFavorites(JSON.parse(savedFavorites));
        } catch (e) {
          console.error('[StaffDirectory] Error parsing favorites:', e);
        }
      }
    } catch (e) {
      console.error('[StaffDirectory] Error parsing user profile:', e);
    }
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    fetchAllStaff();
    fetchDepartments();
    fetchSummary();
    fetchUpcomingBirthdays();
  }, [fetchAllStaff, fetchDepartments, fetchSummary, fetchUpcomingBirthdays]);
  
  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('staffFavorites', JSON.stringify(favorites));
  }, [favorites]);
  
  // Apply filters and sorting
  useEffect(() => {
    console.debug('[StaffDirectory] Applying filters to', staffList.length, 'staff members');
    
    let filtered = [...staffList];
    
    // Search filter
    if (searchTerm && searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(staff => 
        (staff.full_name?.toLowerCase().includes(term)) ||
        (staff.phone?.toLowerCase().includes(term)) ||
        (staff.email?.toLowerCase().includes(term)) ||
        (staff.department?.toLowerCase().includes(term)) ||
        (staff.surname?.toLowerCase().includes(term)) ||
        (staff.other_names?.toLowerCase().includes(term))
      );
      console.debug(`[StaffDirectory] Search filter: ${term} -> ${filtered.length} results`);
    }
    
    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(staff => staff.department === departmentFilter);
      console.debug(`[StaffDirectory] Department filter: ${departmentFilter} -> ${filtered.length} results`);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(staff => staff.status === statusFilter);
      console.debug(`[StaffDirectory] Status filter: ${statusFilter} -> ${filtered.length} results`);
    }
    
    // Employment type filter
    if (employmentTypeFilter !== 'all') {
      if (employmentTypeFilter === 'senior') {
        filtered = filtered.filter(staff => (staff.years_of_experience || 0) >= 5);
      } else if (employmentTypeFilter === 'intermediate') {
        filtered = filtered.filter(staff => (staff.years_of_experience || 0) >= 2 && (staff.years_of_experience || 0) < 5);
      } else if (employmentTypeFilter === 'entry') {
        filtered = filtered.filter(staff => (staff.years_of_experience || 0) < 2);
      }
    }
    
    // Salary range filter
    if (salaryRange[0] > 0 || salaryRange[1] < 1000000) {
      filtered = filtered.filter(staff => {
        const salary = parseFloat(staff.current_salary) || 0;
        return salary >= salaryRange[0] && salary <= salaryRange[1];
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = (a.full_name || `${a.surname} ${a.other_names}`).toLowerCase();
          bVal = (b.full_name || `${b.surname} ${b.other_names}`).toLowerCase();
          break;
        case 'department':
          aVal = (a.department || '').toLowerCase();
          bVal = (b.department || '').toLowerCase();
          break;
        case 'salary':
          aVal = parseFloat(a.current_salary) || 0;
          bVal = parseFloat(b.current_salary) || 0;
          break;
        case 'experience':
          aVal = parseFloat(a.years_of_experience) || 0;
          bVal = parseFloat(b.years_of_experience) || 0;
          break;
        case 'date':
          aVal = new Date(a.date_created).getTime() || 0;
          bVal = new Date(b.date_created).getTime() || 0;
          break;
        case 'age':
          aVal = parseInt(a.age) || 0;
          bVal = parseInt(b.age) || 0;
          break;
        default:
          aVal = (a.full_name || `${a.surname} ${a.other_names}`).toLowerCase();
          bVal = (b.full_name || `${b.surname} ${b.other_names}`).toLowerCase();
      }
      
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });
    
    setFilteredStaff(filtered);
    setPage(0);
    console.debug(`[StaffDirectory] Filtered to ${filtered.length} staff members`);
  }, [staffList, searchTerm, departmentFilter, statusFilter, employmentTypeFilter, sortBy, sortOrder, salaryRange]);
  
  // ==================== HANDLERS ====================
  
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  
  const toggleFavorite = (staffId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(staffId)
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId];
      logSecurityEvent('TOGGLE_FAVORITE', { staffId, action: prev.includes(staffId) ? 'remove' : 'add' });
      return newFavorites;
    });
  };
  
  const handleExportCSV = () => {
    console.debug('[StaffDirectory] Exporting to CSV');
    
    try {
      const headers = [
        'S/N', 'Full Name', 'Phone Number', 'Email Address', 
        'Department', 'Status', 'Salary', 'Years of Experience',
        'Age', 'State of Origin', 'LGA', 'Date Joined'
      ];
      
      const rows = filteredStaff.map((staff, idx) => [
        idx + 1,
        staff.full_name || `${staff.surname} ${staff.other_names}`,
        staff.phone || 'N/A',
        staff.email || 'N/A',
        staff.department || 'N/A',
        staff.status || 'Active',
        staff.current_salary || 0,
        staff.years_of_experience || 'N/A',
        staff.age || 'N/A',
        staff.state_of_origin || 'N/A',
        staff.lga || 'N/A',
        formatDate(staff.date_created)
      ]);
      
      // Escape CSV values properly
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      
      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');
      
      // Add UTF-8 BOM for proper encoding
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `staff_directory_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({ 
        open: true, 
        message: `Successfully exported ${filteredStaff.length} staff records`, 
        severity: 'success' 
      });
      
      logSecurityEvent('EXPORT_CSV', { recordCount: filteredStaff.length });
    } catch (err) {
      console.error('[StaffDirectory] Export error:', err);
      setSnackbar({ open: true, message: 'Export failed', severity: 'error' });
    }
  };
  
  const handleExportPDF = () => {
    setSnackbar({ open: true, message: 'PDF export feature coming soon', severity: 'info' });
  };
  
  const handleExportExcel = () => {
    setSnackbar({ open: true, message: 'Excel export feature coming soon', severity: 'info' });
  };
  
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };
  
  const clearAllFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setStatusFilter('all');
    setEmploymentTypeFilter('all');
    setSalaryRange([0, 1000000]);
    setSortBy('name');
    setSortOrder('asc');
    setPage(0);
    setShowAdvancedFilters(false);
    fetchAllStaff();
    logSecurityEvent('CLEAR_FILTERS', {});
    setSnackbar({ open: true, message: 'All filters cleared', severity: 'success' });
  };
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  
  const handleImageError = (staffId) => {
    console.debug(`[StaffDirectory] Image failed to load for staff ID: ${staffId}`);
    setImageErrors(prev => ({ ...prev, [staffId]: true }));
  };
  
  // ==================== RENDER COMPONENTS ====================
  
  const StatCard = ({ title, value, color, icon: Icon, trend, trendValue }) => (
    <Card sx={{
      borderRadius: 3,
      height: '100%',
      background: `linear-gradient(135deg, ${color}08 0%, ${color}02 100%)`,
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, letterSpacing: '0.5px' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: color, mt: 0.5, fontFamily: 'monospace' }}>
              {value}
            </Typography>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 12, color: trend > 0 ? '#4caf50' : '#f44336' }} />
                <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                  {trend > 0 ? '+' : ''}{trend}%
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ 
            bgcolor: alpha(color, 0.1), 
            color: color, 
            width: 48, 
            height: 48,
            '& .MuiSvgIcon-root': { fontSize: 28 }
          }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
  
  const StaffCard = ({ staff }) => {
    const deptColor = getDepartmentColor(staff.department);
    const isFavorite = favorites.includes(staff.id);
    const hasImageError = imageErrors[staff.id];
    // Get the correct avatar URL - prioritize profile_photo_url from the staff object
    const avatarUrl = getAvatarUrl(staff.profile_photo_url, staff.full_name || `${staff.surname} ${staff.other_names}`, staff.department);
    
    console.debug(`[StaffDirectory] StaffCard rendering for ${staff.full_name}, image URL: ${avatarUrl.substring(0, 100)}...`);
    
    return (
      <Grow in timeout={300}>
        <Card sx={{
          borderRadius: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[12],
            '& .action-buttons': {
              opacity: 1,
            }
          },
          overflow: 'visible',
        }}
        onClick={() => fetchStaffByPhone(staff.phone)}
        >
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height="140"
              image={hasImageError ? getAvatarUrl(null, staff.full_name || `${staff.surname} ${staff.other_names}`, staff.department) : avatarUrl}
              alt={staff.full_name || `${staff.surname} ${staff.other_names}`}
              sx={{
                objectFit: 'cover',
                bgcolor: deptColor.light,
              }}
              onError={() => handleImageError(staff.id)}
            />
            <Box sx={{
              position: 'absolute',
              bottom: -20,
              left: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              zIndex: 1,
            }}>
              <Avatar
                src={hasImageError ? null : avatarUrl}
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: deptColor.primary,
                  fontSize: '1rem'
                }}
                imgProps={{ 
                  onError: () => {
                    console.debug(`[StaffDirectory] Avatar failed to load for ${staff.id}`);
                    handleImageError(staff.id);
                  } 
                }}
              >
                {getInitials(staff.full_name || `${staff.surname} ${staff.other_names}`)}
              </Avatar>
              <Chip
                label={staff.status || 'Active'}
                size="small"
                color={getStatusColor(staff.status)}
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: 'white',
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white', transform: 'scale(1.1)' },
                transition: 'transform 0.2s',
                zIndex: 2,
              }}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(staff.id);
              }}
            >
              {isFavorite ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
            </IconButton>
          </Box>
          
          <CardContent sx={{ flexGrow: 1, pt: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, fontSize: '1rem' }}>
              {sanitizeString(staff.full_name || `${staff.surname} ${staff.other_names}`)}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: 14 }} />
              {staff.department || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14, color: COLORS.success }} />
              {staff.phone || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <EmailIcon sx={{ fontSize: 14, color: COLORS.info }} />
              <span style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {staff.email || 'N/A'}
              </span>
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MoneyIcon sx={{ fontSize: 14, color: COLORS.secondary }} />
              <strong style={{ color: COLORS.secondary }}>{formatCurrency(staff.current_salary)}</strong>
            </Typography>
          </CardContent>
          
          <Divider sx={{ mx: 2 }} />
          
          <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {staff.years_of_experience ? `${staff.years_of_experience} yrs exp` : 'Entry Level'}
              </Typography>
            </Box>
            <Tooltip title="View full profile">
              <Button
                size="small"
                variant="contained"
                sx={{ 
                  textTransform: 'none', 
                  borderRadius: 2,
                  bgcolor: deptColor.primary,
                  '&:hover': { bgcolor: deptColor.primary, opacity: 0.9 }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  fetchStaffByPhone(staff.phone);
                }}
              >
                View Details
              </Button>
            </Tooltip>
          </CardActions>
        </Card>
      </Grow>
    );
  };
  
  const StaffTableRow = ({ staff, index }) => {
    const deptColor = getDepartmentColor(staff.department);
    const hasImageError = imageErrors[staff.id];
    const avatarUrl = getAvatarUrl(staff.profile_photo_url, staff.full_name || `${staff.surname} ${staff.other_names}`, staff.department);
    
    console.debug(`[StaffDirectory] StaffTableRow rendering for ${staff.full_name}, avatar URL: ${avatarUrl.substring(0, 100)}...`);
    
    return (
      <TableRow
        hover
        sx={{ 
          cursor: 'pointer',
          '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) },
          transition: 'background-color 0.2s'
        }}
        onClick={() => fetchStaffByPhone(staff.phone)}
      >
        <TableCell sx={{ fontWeight: 500 }}>{index}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={hasImageError ? null : avatarUrl}
              sx={{
                width: 56,
                height: 56,
                border: `3px solid white`,
                bgcolor: deptColor.primary,
                boxShadow: theme.shadows[3],
              }}
              imgProps={{ 
                onError: () => {
                  console.debug(`[StaffDirectory] Table row avatar failed to load for ${staff.id}`);
                  handleImageError(staff.id);
                }
              }}
            >
              {getInitials(staff.full_name || `${staff.surname} ${staff.other_names}`)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {sanitizeString(staff.full_name || `${staff.surname} ${staff.other_names}`)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ID: {staff.id}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={staff.department || 'N/A'}
            size="small"
            sx={{ 
              bgcolor: alpha(deptColor.primary, 0.1), 
              color: deptColor.primary, 
              fontWeight: 500,
              '& .MuiChip-label': { px: 1 }
            }}
          />
        </TableCell>
        <TableCell>{staff.phone || 'N/A'}</TableCell>
        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {staff.email || 'N/A'}
        </TableCell>
        <TableCell>
          <Chip
            label={staff.years_of_experience ? `${staff.years_of_experience} yrs` : 'Entry'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold" color={COLORS.secondary}>
            {formatCurrency(staff.current_salary)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={staff.status || 'Active'}
            size="small"
            color={getStatusColor(staff.status)}
            sx={{ fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>
          <Tooltip title="View full profile">
            <IconButton
              size="small"
              sx={{ 
                color: deptColor.primary,
                '&:hover': { bgcolor: alpha(deptColor.primary, 0.1) }
              }}
              onClick={(e) => {
                e.stopPropagation();
                fetchStaffByPhone(staff.phone);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };
  
  const StaffDetailsDrawer = () => {
    if (!selectedStaff) return null;
    
    const staff = selectedStaff;
    const deptColor = getDepartmentColor(staff.department);
    const hasImageError = imageErrors[`drawer_${staff.id}`];
    // Get the avatar URL for the drawer - use the profile_photo_url from selectedStaff
    const drawerAvatarUrl = getAvatarUrl(staff.profile_photo_url, `${staff.surname} ${staff.other_names}`, staff.department);
    
    console.debug(`[StaffDirectory] StaffDetailsDrawer rendering for ${staff.surname} ${staff.other_names}, image URL: ${drawerAvatarUrl.substring(0, 100)}...`);
    
    // Section styling
    const sectionTitleSx = {
      variant: 'subtitle1',
      fontWeight: 'bold',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      color: deptColor.primary,
    };
    
    const infoPaperSx = {
      variant: 'outlined',
      sx: { p: 1.5, borderRadius: 2, bgcolor: alpha(deptColor.primary, 0.02) }
    };
    
    return (
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={() => setDetailsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 550,
            borderRadius: isMobile ? 0 : '20px 0 0 20px',
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header with cover image */}
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                height: 200,
                background: deptColor.gradient,
                position: 'relative',
              }}
            />
            <IconButton
              onClick={() => setDetailsDrawerOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Avatar
              src={hasImageError ? null : drawerAvatarUrl}
              sx={{
                position: 'absolute',
                bottom: -40,
                left: 24,
                width: 96,
                height: 96,
                border: '4px solid white',
                bgcolor: deptColor.primary,
                boxShadow: theme.shadows[4],
              }}
              imgProps={{ 
                onError: () => {
                  console.debug(`[StaffDirectory] Drawer avatar failed to load for staff ID: ${staff.id}`);
                  setImageErrors(prev => ({ ...prev, [`drawer_${staff.id}`]: true }));
                }
              }}
            >
              <PersonIcon sx={{ fontSize: 50 }} />
            </Avatar>
            <Chip
              label={staff.status || 'Active'}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                bgcolor: 'white',
                fontWeight: 'bold',
              }}
              color={getStatusColor(staff.status)}
            />
          </Box>
          
          {/* Content */}
          <Box sx={{ p: 3, pt: 5, flexGrow: 1, overflow: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
              {sanitizeString(staff.surname)} {sanitizeString(staff.other_names)}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {staff.department || 'Department not assigned'}
            </Typography>
            
            {/* Quick stats chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                icon={<CalendarIcon />} 
                label={`Joined: ${formatRelativeTime(staff.date_created)}`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                icon={<CakeIcon />} 
                label={`Age: ${staff.age || 'N/A'}`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                icon={<WorkIcon />} 
                label={`Exp: ${staff.years_of_experience || 0} years`} 
                size="small" 
                variant="outlined" 
              />
              {staff.marital_status && (
                <Chip 
                  icon={<BadgeIcon />} 
                  label={staff.marital_status} 
                  size="small" 
                  variant="outlined" 
                />
              )}
            </Stack>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Contact Information */}
            <Typography {...sectionTitleSx}>
              <PhoneIcon fontSize="small" /> Contact Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Paper {...infoPaperSx}>
                  <Typography variant="caption" color="textSecondary">Phone Number</Typography>
                  <Typography variant="body2" fontWeight="medium">{staff.phone || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper {...infoPaperSx}>
                  <Typography variant="caption" color="textSecondary">Email Address</Typography>
                  <Typography variant="body2" fontWeight="medium">{staff.email || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper {...infoPaperSx}>
                  <Typography variant="caption" color="textSecondary">Residential Address</Typography>
                  <Typography variant="body2">{staff.residential_address || staff.hometown_permanent_address || 'N/A'}</Typography>
                </Paper>
              </Grid>
              {staff.state_of_origin && (
                <Grid item xs={6}>
                  <Paper {...infoPaperSx}>
                    <Typography variant="caption" color="textSecondary">State of Origin</Typography>
                    <Typography variant="body2">{staff.state_of_origin}</Typography>
                  </Paper>
                </Grid>
              )}
              {staff.lga && (
                <Grid item xs={6}>
                  <Paper {...infoPaperSx}>
                    <Typography variant="caption" color="textSecondary">LGA</Typography>
                    <Typography variant="body2">{staff.lga}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
            
            {/* Banking Information */}
            <Typography {...sectionTitleSx}>
              <BankIcon fontSize="small" /> Banking & Salary
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Paper {...infoPaperSx}>
                  <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                  <Typography variant="body2">{staff.bank_name || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper {...infoPaperSx}>
                  <Typography variant="caption" color="textSecondary">Account Number</Typography>
                  <Typography variant="body2" fontFamily="monospace">{staff.account_number || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper {...infoPaperSx} sx={{ bgcolor: alpha(COLORS.secondary, 0.05) }}>
                  <Typography variant="caption" color="textSecondary">Current Salary</Typography>
                  <Typography variant="h6" fontWeight="bold" color={COLORS.secondary}>
                    {formatCurrency(staff.current_salary)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Education & Experience */}
            <Accordion defaultExpanded sx={{ borderRadius: 2, mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">🎓 Education Background</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Primary:</strong> {staff.primary_school_name || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Secondary:</strong> {staff.secondary_school_name || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tertiary:</strong> {staff.tertiary_school_name || 'N/A'}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
            
            <Accordion sx={{ borderRadius: 2, mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">💼 Work Experience</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Experience Type:</strong> {staff.experience_type || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Years of Experience:</strong> {staff.years_of_experience || 0} years
                  </Typography>
                  <Typography variant="body2">
                    <strong>Previous Workplace 1:</strong> {staff.last_workplace_one || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Previous Workplace 2:</strong> {staff.last_workplace_two || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Salary Expectation:</strong> {formatCurrency(staff.salary_expectation)}
                  </Typography>
                  {staff.self_evaluation && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: alpha(COLORS.background, 0.5), borderRadius: 2 }}>
                      <Typography variant="caption" color="textSecondary">Self Evaluation</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{staff.self_evaluation}</Typography>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
            
            {/* Previous Employment */}
            {(staff.previous_employer || staff.job_title) && (
              <Accordion sx={{ borderRadius: 2, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">📋 Previous Employment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <strong>Employer:</strong> {staff.previous_employer || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Job Title:</strong> {staff.job_title || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Employment Period:</strong> {formatDate(staff.employment_start_date)} - {formatDate(staff.employment_end_date) || 'Present'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Reason for Leaving:</strong> {staff.reason_for_leaving || 'N/A'}
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Next of Kin */}
            {staff.next_of_kin_name && (
              <Accordion sx={{ borderRadius: 2, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">👨‍👩‍👧 Next of Kin</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {staff.next_of_kin_name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Relationship:</strong> {staff.relationship || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Contact:</strong> {staff.next_of_kin_contact || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Address:</strong> {staff.next_of_kin_address || 'N/A'}
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Recent Activity */}
            {(staff.recent_deductions?.length > 0 || staff.recent_bonuses?.length > 0) && (
              <Accordion sx={{ borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">📊 Recent Activity</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {staff.recent_bonuses?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={COLORS.secondary} sx={{ fontWeight: 600 }}>
                        Bonuses Received
                      </Typography>
                      {staff.recent_bonuses.map((bonus, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: idx === staff.recent_bonuses.length - 1 ? 'none' : `1px solid ${COLORS.border}` }}>
                          <Typography variant="body2">{bonus.reason || 'Bonus'}</Typography>
                          <Typography variant="body2" fontWeight="bold" color={COLORS.secondary}>
                            +{formatCurrency(bonus.bonus_amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {staff.recent_deductions?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color={COLORS.error} sx={{ fontWeight: 600 }}>
                        Deductions
                      </Typography>
                      {staff.recent_deductions.map((ded, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: idx === staff.recent_deductions.length - 1 ? 'none' : `1px solid ${COLORS.border}` }}>
                          <Typography variant="body2">{ded.remark || 'Deduction'}</Typography>
                          <Typography variant="body2" fontWeight="bold" color={COLORS.error}>
                            -{formatCurrency(ded.amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>
      </Drawer>
    );
  };
  
  // ==================== MAIN RENDER ====================
  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: COLORS.background }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: COLORS.background }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%',
        overflow: 'auto',
        height: '100vh',
        transition: 'margin-left 0.3s ease-in-out',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.debug('[StaffDirectory] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />
        
        {/* Hero Header */}
        <Paper elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
            <GroupIcon sx={{ fontSize: 200 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Staff Directory
              </Typography>
             
              <Breadcrumbs sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }} separator={<ChevronRightIcon fontSize="small" />}>
                <Link href="#" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  Home
                </Link>
                <Typography color="white">Staff Directory</Typography>
              </Breadcrumbs>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={() => {
                  setSearchTerm('');
                  fetchAllStaff();
                }}
                disabled={loading}
                sx={{ bgcolor: 'white', color: COLORS.primary, '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportMenuOpen}
                disabled={filteredStaff.length === 0}
                sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: '#f5f5f5', bgcolor: 'rgba(255,255,255,0.1)' }, textTransform: 'none' }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleExportCSV}>
                  <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export as CSV
                </MenuItem>
                <MenuItem onClick={handleExportExcel}>
                  <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export as Excel
                </MenuItem>
                <MenuItem onClick={handleExportPDF}>
                  <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export as PDF
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Paper>
        
        {/* Summary Statistics Cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Total Active Staff"
                value={summary.total_active_staff || 0}
                color={COLORS.primary}
                icon={GroupIcon}
                trend={5}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Departments"
                value={summary.staff_by_department?.length || 0}
                color={COLORS.success}
                icon={BusinessIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Avg. Salary"
                value={summary.salary_stats?.avg || '₦0'}
                color={COLORS.secondary}
                icon={MoneyIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Avg. Age"
                value={`${summary.average_age || 0} yrs`}
                color={COLORS.accent}
                icon={TimelineIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Salary Budget"
                value={summary.salary_stats?.total_budget || '₦0'}
                color={COLORS.error}
                icon={AssessmentIcon}
              />
            </Grid>
          </Grid>
        )}
        
        {/* Upcoming Birthdays Alert */}
        {upcomingBirthdays.length > 0 && (
          <Alert
            severity="info"
            icon={<CakeIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setActiveTab(2)}>
                View All
              </Button>
            }
          >
            <AlertTitle>🎂 Upcoming Birthdays</AlertTitle>
            {upcomingBirthdays.slice(0, 3).map((bday, idx) => (
              <Typography key={idx} variant="body2">
                • {bday.name} ({bday.department}) - {bday.birthday}
              </Typography>
            ))}
            {upcomingBirthdays.length > 3 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                and {upcomingBirthdays.length - 3} more...
              </Typography>
            )}
          </Alert>
        )}
        
        {/* Filters and Controls */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, phone, email, or department..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  searchStaff(value);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: COLORS.textSecondary }} /></InputAdornment>,
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => {
                        setSearchTerm('');
                        searchStaff('');
                      }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  label="Department"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.departments}>
                      {dept.departments} ({dept.staff_count || 0})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="salary">Salary</MenuItem>
                  <MenuItem value="experience">Experience</MenuItem>
                  <MenuItem value="age">Age</MenuItem>
                  <MenuItem value="date">Date Joined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1}>
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {sortOrder === 'asc' ? 'ASC' : 'DESC'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <ButtonGroup fullWidth variant="outlined" size="medium" sx={{ borderRadius: 2 }}>
                <Button
                  onClick={() => setViewMode('table')}
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  sx={{ 
                    textTransform: 'none',
                    bgcolor: viewMode === 'table' ? COLORS.primary : 'transparent',
                    color: viewMode === 'table' ? 'white' : COLORS.text,
                    '&:hover': viewMode === 'table' ? { bgcolor: COLORS.primaryDark } : {}
                  }}
                >
                  <TableChartIcon fontSize="small" sx={{ mr: 0.5 }} /> Table
                </Button>
                <Button
                  onClick={() => setViewMode('card')}
                  variant={viewMode === 'card' ? 'contained' : 'outlined'}
                  sx={{ 
                    textTransform: 'none',
                    bgcolor: viewMode === 'card' ? COLORS.primary : 'transparent',
                    color: viewMode === 'card' ? 'white' : COLORS.text,
                    '&:hover': viewMode === 'card' ? { bgcolor: COLORS.primaryDark } : {}
                  }}
                >
                  <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} /> Cards
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
          
          {/* Advanced Filters Toggle */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              sx={{ textTransform: 'none' }}
            >
              {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
            </Button>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              sx={{ textTransform: 'none', color: COLORS.error }}
            >
              Clear All Filters
            </Button>
          </Box>
          
          {/* Advanced Filters Panel */}
          <Collapse in={showAdvancedFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.border}` }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      value={employmentTypeFilter}
                      onChange={(e) => setEmploymentTypeFilter(e.target.value)}
                      label="Employment Type"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">All Levels</MenuItem>
                      <MenuItem value="senior">Senior (5+ years)</MenuItem>
                      <MenuItem value="intermediate">Intermediate (2-4 years)</MenuItem>
                      <MenuItem value="entry">Entry Level (&lt;2 years)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                    Salary Range (₦)
                  </Typography>
                  <Slider
                    value={salaryRange}
                    onChange={(e, newValue) => setSalaryRange(newValue)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={1000000}
                    step={50000}
                    valueLabelFormat={(value) => formatCurrency(value)}
                    sx={{ color: COLORS.primary }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption">{formatCurrency(salaryRange[0])}</Typography>
                    <Typography variant="caption">{formatCurrency(salaryRange[1])}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
        
        {/* Results Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredStaff.length} of {totalStaff} staff members
          </Typography>
          <Chip
            label={`${filteredStaff.length} results`}
            size="small"
            sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary }}
          />
        </Box>
        
        {/* Staff Display - Table View (Default) */}
        {viewMode === 'table' && (
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden', 
            boxShadow: theme.shadows[2],
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 400px)',
            minHeight: 400
          }}>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="medium" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.05) }}>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Staff Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Experience</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Salary</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(COLORS.primary, 0.05) }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <CircularProgress sx={{ color: COLORS.primary }} />
                        <Typography variant="body2" sx={{ mt: 1, color: COLORS.textSecondary }}>
                          Loading staff data...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredStaff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <PersonIcon sx={{ fontSize: 48, color: COLORS.textSecondary, mb: 1 }} />
                        <Typography variant="body1" color="textSecondary">No staff members found</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Try adjusting your search or filter criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStaff
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((staff, idx) => (
                        <StaffTableRow 
                          key={staff.id} 
                          staff={staff} 
                          index={page * rowsPerPage + idx + 1} 
                        />
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredStaff.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 15, 25, 50, 100]}
              sx={{ borderTop: `1px solid ${COLORS.border}` }}
            />
          </Paper>
        )}
        
        {/* Staff Display - Card View */}
        {viewMode === 'card' && (
          <>
            <Grid container spacing={3}>
              {loading ? (
                Array.from(new Array(rowsPerPage)).map((_, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                    <Card sx={{ borderRadius: 3 }}>
                      <Skeleton variant="rectangular" height={140} />
                      <CardContent>
                        <Skeleton variant="text" width="80%" height={32} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="70%" />
                        <Skeleton variant="text" width="50%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : filteredStaff.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
                    <PersonIcon sx={{ fontSize: 64, color: COLORS.textSecondary, mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No staff members found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Try adjusting your search or filter criteria
                    </Typography>
                  </Paper>
                </Grid>
              ) : (
                filteredStaff
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((staff) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={staff.id}>
                      <StaffCard staff={staff} />
                    </Grid>
                  ))
              )}
            </Grid>
            {filteredStaff.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={filteredStaff.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 15, 25, 50, 100]}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
        
        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            © {new Date().getFullYear()} Elegantè IT - Staff Directory. All rights reserved.
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Version 2.0 | Secure Staff Information System
          </Typography>
        </footer>
      </Box>
      
      {/* Staff Details Drawer */}
      <StaffDetailsDrawer />
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffDirectory;